<template>
  <div class="menu">
    <pull-down />
    <menu-button
      v-if="!isInfoUrlFrameVisible"
      :class="{ 'bottom-position': currentConfig.isBottomNavigationEnabled }"
      icon="icons/close.svg"
      class="close-icon"
      @click="$emit('close')"
    />
    <div class="layout">
      <div
        class="container"
        @click.exact="$emit('close')"
      >
        <div class="languages">
          <div
            v-if="contentLanguages.length > 1"
            class="list"
          >
            <menu-language-item
              v-for="lang in contentLanguages"
              :key="lang.code"
              :current-language="currentLanguage"
              :code="lang.code"
              :name="lang.name"
              @select="handleSelectLang"
            />
          </div>
        </div>
      </div>

      <div class="bottom-panel">
        <div class="controls">
          <div
            class="restart-flow-button"
            @click="handleRestartButton"
          >
            <img
              src="@/assets/home.svg"
              class="home-icon"
            >
            <div>BACK TO START</div>
          </div>
        </div>

        <div class="footer">
          <div class="logo">
            <img
              src="@/assets/pluralio-symbol.svg"
              class="logo-icon"
            >
          </div>
          <div class="links">
            <a
              href=""
              @click.prevent="showInfoUrlFrame('https://plural.io/avatars')"
            >
              Learn more
            </a>
            <div class="separator">
              •
            </div>
            <a
              href=""
              @click.prevent="showInfoUrlFrame(privacyLink)"
            >
              Privacy Policy
            </a>
            <div class="separator">
              •
            </div>
            <a
              href="#"
              @click="isDebugModalOpen = true"
            >
              Debug
            </a>
          </div>
        </div>
      </div>
    </div>

    <info-url-frame-component
      v-if="isInfoUrlFrameVisible"
      :info-url="infoUrl"
      @close="onInfoUrlFrameClose"
    />
    <debug-modal
      v-if="isDebugModalOpen"
      @hide="isDebugModalOpen = false"
    />
  </div>
</template>

<script>
import { defineComponent, ref, computed } from 'vue';
import MenuLanguageItem from '@/components/MenuLanguageItem';
import PullDown from '@/components/PullDown';
import MenuButton from '@/components/MenuButton';
import DebugModal from '@/components/DebugModal';
import useConfig from '@/composables/useConfig';
import InfoUrlFrameComponent from '@/components/InfoUrlFrame';

export default defineComponent({
  name: 'MenuPage',

  emits: ['close', 'restart-flow', 'select-language'],

  components: {
    InfoUrlFrameComponent,
    MenuButton,
    MenuLanguageItem,
    PullDown,
    DebugModal,
  },

  setup(props, { emit }) {
    const {
      currentLanguage,
      currentConfig,
      initialConfig,
      setFlowStateHomeButton,
      setFlowStateUserLanguageSwitch,
    } = useConfig();

    const isDebugModalOpen = ref(false);
    const isInfoUrlFrameVisible = ref(false);
    const infoUrl = ref(null);

    const contentLanguages = computed(() => initialConfig.value.contentLanguages.filter((lang) => lang.active));

    function showInfoUrlFrame(url) {
      infoUrl.value = url;
      isInfoUrlFrameVisible.value = true;
    }

    function onInfoUrlFrameClose() {
      infoUrl.value = '';
      isInfoUrlFrameVisible.value = false;
    }

    function restartFlow() {
      emit('close');
      emit('restart-flow');
    }

    function handleRestartButton() {
      setFlowStateHomeButton();
      restartFlow();
    }

    function handleSelectLang(lang) {
      setFlowStateUserLanguageSwitch();
      emit('select-language', lang);
      restartFlow();
    }

    return {
      currentConfig,
      currentLanguage,
      isDebugModalOpen,
      privacyLink: currentConfig.value.customPayload?.privacy_url || 'https://humanizing.com/privacy',
      restartFlow,
      handleRestartButton,
      handleSelectLang,
      showInfoUrlFrame,
      onInfoUrlFrameClose,
      isInfoUrlFrameVisible,
      infoUrl,
      contentLanguages,
    };
  },
});
</script>

<style lang="scss" scoped>
.menu {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 100000;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);

  .layout {
    position: relative;
    height: 100%;
    display: flex;
    flex-direction: column;

    @media screen and (min-width: $desktop_s_breakpoint) {
      flex-direction: row-reverse;
    }

    @media screen and (max-height: $mobile_height_breakpoint) {
      flex-direction: row-reverse;
    }

    @media screen and (min-height: $big_vertical_screen_breakpoint) {
      flex-direction: column;
    }

    .container {
      height: calc(100% - 276px);
      display: flex;
      justify-content: center;
      align-items: center;
      background: rgba(255, 255, 255, 0.8);
      padding-top: 80px;

      @media screen and (min-width: $desktop_s_breakpoint) {
        width: 50%;
        height: 100%;
        padding-top: 20px;
      }

      @media screen and (max-height: $mobile_height_breakpoint) {
        width: 50%;
        height: 100%;
        padding-top: 20px;
      }

      @media screen and (min-height: $big_vertical_screen_breakpoint) {
        height: calc(100% - 1316px);
        width: 100%;
        padding-top: 180px;
      }

      .languages {
        height: auto;
        width: 100%;
        overflow-y: auto;
        max-height: 100%;

        .list {
          width: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
          flex-direction: column;
          height: auto;
        }
      }
    }

    .bottom-panel {
      width: 100%;
      height: 276px;
      background: #FFFFFF;
      box-shadow: 0 32px 32px -8px rgba(0, 0, 0, 0.08),
                  0 0 32px -8px rgba(0, 0, 0, 0.12),
                  0 0 1px rgba(0, 0, 0, 0.2);
      padding-top: 56px;
      position: relative;

      @media screen and (min-width: $desktop_s_breakpoint) {
        width: 50%;
        height: 100%;
        padding-top: 0;
        display: flex;
        justify-content: center;
        align-items: center;
      }

      @media screen and (max-height: $mobile_height_breakpoint) {
        height: 100%;
        width: 50%;
        padding-top: 0;
        display: flex;
        justify-content: center;
        align-items: center;
      }

      @media screen and (min-height: $big_vertical_screen_breakpoint) {
        height: 1316px;
        width: 100%;
        padding-top: 200px;
      }

      .controls {
        width: 100%;
      }

      .restart-flow-button {
        width: 80%;
        max-width: 584px;
        height: 56px;
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        background: #007CBA;
        border-radius: 8px;
        color: white;
        margin: 0 auto;
        cursor: pointer;
        font-weight: 500;
        font-size: 16px;
        line-height: 100%;

        $sizes:
          $tablet_breakpoint 584px 80px 24px,
          $desktop_s_breakpoint 432px 80px 20px,
          $desktop_m_breakpoint 432px 80px 20px,
          $desktop_l_breakpoint 432px 80px 20px;

        @each $size, $width, $height, $font-size in $sizes {
          @media screen and (min-width: $size) {
            max-width: $width;
            height: $height;
            font-size: $font-size;
          }
        }

        @media screen and (max-height: $mobile_height_breakpoint) {
          max-width: 336px;
          height: 56px;
          font-size: 16px;
        }

        @media screen and (min-height: $big_vertical_screen_breakpoint) {
          max-width: 1700px;
          height: 280px;
          font-size: 64px;
        }
      }

      .footer {
        bottom: 0;
        left: 0;
        position: absolute;
        width: 100%;
        padding-bottom: 20px;

        .logo {
          height: auto;
          width: 100%;
          margin-bottom: 16px;
        }

        .links {
          display: flex;
          justify-content: center;
          align-items: center;
          letter-spacing: 0.25px;
          font-weight: 500;
          font-size: 12px;
          line-height: 16px;
          color: #C6C7C9;
          margin: 0 auto;

          a {
            text-decoration: none;
            color: #C6C7C9;
          }

          .separator {
            margin-left: 8px;
            margin-right: 8px;
          }
        }

        @media screen and (min-width: $tablet_breakpoint) {
          padding-bottom: 24px;

          .links {
            font-size: 14px;
            line-height: 20px;

            .separator {
              margin-left: 10px;
              margin-right: 10px;
            }
          }
        }
      }
    }
  }

  .home-icon {
    width: 32px;
    height: 32px;
    margin-right: 10px;

    @media screen and (min-width: $tablet_breakpoint) {
      margin-right: 16px;
    }

    @media screen and (max-height: $mobile_height_breakpoint) {
      margin-right: 10px;
    }

    @media screen and (min-height: $big_vertical_screen_breakpoint) {
      width: 100px;
      height: 100px;
      margin-right: 40px;
    }
  }
}
.close-icon {
  position: absolute;
  left: 16px;
  top: 10px;

  &.bottom-position {
    top: auto;
    bottom: 10px;
  }

  @media screen and (min-height: $big_vertical_screen_breakpoint) {
    left: 40px;
    top: 20px;
  }
}
</style>
